/**
 * migrator.js
 * -----------
 * Plans and applies schema/data changes using the semantic triplet strategy:
 *   version = store(object)(data) = MAJOR.MINOR.PATCH
 *
 * Rules of thumb:
 * - MAJOR (store) => Dexie store definition change -> requires DB version bump.
 * - MINOR (object) => model change for records -> run entityFactory coercion over all.
 * - PATCH (data) => targeted data upserts/merges (no model change).
 *
 * The migrator keeps a tiny meta-record per store inside a special meta store
 * `__schema` like { id: <storeName>, version: "x.y.z", modified: Date }.
 */

// import Dexie from "dexie";
import { createEntity, coerceToModel } from "../entityFactory";
// import { defaultModels } from "./models";
import {
  parseTriplet,
  cmpTriplet,
  isValidTriplet,
  extractVersionFromFilename,
} from "./configLoader";
import { appendHistory, deriveBumpType } from "./history";
import { applyTransform, keyPruning } from "./schemaHooks";

const META = "__schema";

/**
 * Builds a combined Dexie store definition object.
 * For each store name, the entry with the highest MAJOR (and then by full version) wins.
 *
 * @param {Array<{version:string, stores:Object<string,string>}>} configs - Normalized configs.
 * @returns {Object<string,string>} Dexie `.stores()` definition including the META store.
 */
export function buildDexieStoresFromConfigs(configs) {
  const latest = new Map(); // storeName -> { version, def }
  for (const cfg of configs) {
    const t = parseTriplet(cfg.version);
    for (const [store, def] of Object.entries(cfg.stores)) {
      const prev = latest.get(store);
      if (
        !prev ||
        t.major > parseTriplet(prev.version).major ||
        (t.major === parseTriplet(prev.version).major &&
          cmpTriplet(cfg.version, prev.version) > 0)
      ) {
        latest.set(store, { version: cfg.version, def });
      }
    }
  }
  const stores = { [META]: "id" };
  for (const [store, { def }] of latest) stores[store] = def;
  return stores;
}

/**
 * Returns an ordered timeline (ascending by version) of configs for a specific store.
 *
 * @param {Array<{version:string, stores:Object<string,string>, model?:Object, data?:Object[]}>} configs
 * @param {string} storeName
 * @returns {Array} Sorted subset of configs that reference `storeName`.
 */
export function getStoreTimeline(configs, storeName) {
  return configs
    .filter((c) => c.stores[storeName])
    .sort((a, b) => cmpTriplet(a.version, b.version));
}

/**
 * Returns the last applied version for a store.
 * Preference order:
 *   1) store's own meta row (id === store) if it has a valid semver
 *   2) __schema[id] if valid
 *   3) seed __schema[id] to 0.0.0 ONCE (only if missing)
 */
// BEFORE (your version wrote __schema "0.0.0" in step 3)
// async function getPrevVersion(db, store) { ... add({id, "0.0.0"}) ... return "0.0.0"; }

// AFTER — NO WRITES HERE
async function getPrevVersion(db, store) {
  // 1) Store’s own meta row (id === store) wins if valid
  try {
    const meta = await db.table(store).get(store);
    if (meta?.version && isValidTriplet(meta.version)) {
      return meta.version;
    }
  } catch (_) {}

  // 2) __schema fallback if valid
  try {
    const row = await db.table(META).get(store);
    if (row?.version && isValidTriplet(row.version)) {
      return row.version;
    }
  } catch (_) {}

  // 3) No side-effects: for fresh DB, just report "0.0.0"
  return "0.0.0";
}

//previous
// async function getPrevVersion(db, store) {
//   // 1) Try the store's own meta row
//   try {
//     const storeMeta = await db.table(store).get(store);
//     if (storeMeta?.version && isValidTriplet(storeMeta.version)) {
//       return storeMeta.version;
//     }
//   } catch (_) {}

//   // 2) Try __schema
//   const schemaRow = await db.table(META).get(store);
//   if (schemaRow?.version && isValidTriplet(schemaRow.version)) {
//     return schemaRow.version;
//   }

//   // 3) Seed __schema ONLY IF missing (do NOT overwrite!)
//   try {
//     await db
//       .table(META)
//       .add({ id: store, version: "0.0.0", modified: Date.now() });
//   } catch (_) {
//     // add() will throw if it already exists; ignore
//   }
//   return "0.0.0";
// }

/**
 * Persists the chosen version to both __schema and the store's meta row.
 */
async function setVersion(db, store, version, schemaSig) {
  await db.table("__schema").put(
    {
      id: store,
      version,
      schemaSignature: schemaSig ?? "",
      modified: Date.now(),
    },
    store
  );
  try {
    const meta = await db.table(store).get(store);
    if (meta) {
      meta.version = version;
      meta.modified = Date.now();
      await db.table(store).put(meta);
    }
  } catch (_) {}
}

// previous
// async function setVersion(db, store, version) {
//   // __schema record stores version in __schema
//   await db.table(META).put({ id: store, version, modified: Date.now() }, store);

//   // record version in store's own meta row (id === store)
//   try {
//     const meta = await db.table(store).get(store);

//     if (meta) {
//       meta.version = version;
//       meta.modified = Date.now();
//       await db.table(store).put(meta);
//     }
//   } catch (_) {}
// }

// previous
// /**
//  * Ensures the META record exists for a store, returning its previous recorded version.
//  *
//  * @param {Dexie} db
//  * @param {string} store
//  * @returns {Promise<string>} Previously recorded version or "0.0.0".
//  */
// async function getPrevVersion(db, store) {
//   await db
//     .table(META)
//     .put({ id: store, version: "0.0.0", modified: Date.now() }, store);
//   const rec = await db.table(META).get(store);
//   return rec?.version || "0.0.0";
// }

// /**
//  * Writes the META version for a store.
//  * @param {Dexie} db
//  * @param {string} store
//  * @param {string} version
//  * @returns {Promise<void>}
//  */
// async function setVersion(db, store, version) {
//   await db.table(META).put({ id: store, version, modified: Date.now() }, store);
// }

/**
 * Applies one config step for a given store:
 * - First install (empty store + data present): populate storeObject + initial data.
 * - MINOR (model) change: coerce all existing records to the new dataObject shape.
 * - PATCH (data) change: shallow upsert any records in `cfg.data`.
 *
 * Also updates the storeObject.version (if storeObject template exists).
 *
 * @param {Dexie} db
 * @param {string} store
 * @param {{version:string, model?:{storeObject?:Object, dataObject?:Object}, data?:Object[]}} cfg
 * @param {{[store:string]:{storeObject?:Object, dataObject?:Object}}} models - Aggregated model templates (latest wins).
 * @returns {Promise<void>}
 */

/**
 * applyConfigStep
 * - Applies a single normalized config step to one store.
 * - Respects optional hooks: beforePopulate, afterPopulate, transform (data rows only).
 * - Never applies the data-row transform to the storeObject meta row.
 * - StoreObject model changes (coerce + prune) run ONLY on MAJOR/MINOR updates.
 * - If a PATCH includes storeObject {id: store}, it updates the storeObject (values), not via transform.
 */
async function applyConfigStep(db, store, cfg, chosenVersion, opts = {}) {
  const { bumpType = null } = opts;

  const model =
    cfg.model && (cfg.model.storeObject || cfg.model.dataObject)
      ? cfg.model
      : {};

  const hooks = cfg.hooks || {};
  const transform = hooks.transform; // single generic hook for data rows
  const utils = { keyPruning };

  // Helpers to coerce with current templates (no global dropping)
  const coerceData = (obj) =>
    model.dataObject
      ? coerceToModel(model.dataObject, obj, { dropUnknown: false })
      : obj;

  const coerceStore = (obj) =>
    model.storeObject
      ? coerceToModel(model.storeObject, obj, { dropUnknown: false })
      : obj;

  // Should we apply storeObject model changes this step?
  const allowStoreModelChange = bumpType === "major" || bumpType === "minor";

  await db.transaction("rw", db.table(store), async () => {
    const count = await db.table(store).count();

    /** POPULATE */
    if (count === 0 && Array.isArray(cfg.data) && cfg.data.length) {
      if (typeof hooks.beforePopulate === "function") {
        await hooks.beforePopulate({ db, store, version: chosenVersion, cfg });
      }

      // Insert storeObject first (meta row, id === store)
      if (model.storeObject) {
        const metaId = store;
        const provided = cfg.data.find((d) => d.id === metaId) || {
          id: metaId,
          label: store,
          version: chosenVersion,
          created: Date.now(),
          modified: Date.now(),
        };
        const storeObj = createEntity(model.storeObject, provided, {
          required: ["id"],
        });
        await db.table(store).put(storeObj);
      }

      // Insert data rows (skip storeObject id)
      for (const rec of cfg.data) {
        if (rec.id === store) continue; // never transform the storeObject as data
        const seeded = model.dataObject
          ? createEntity(model.dataObject, rec, { required: ["id"] })
          : rec;

        const shaped = applyTransform(seeded, transform, {
          store,
          stepVersion: cfg.version,
          model: model.dataObject,
          utils,
        });

        await db.table(store).put(shaped);
      }

      // Post-populate pass: normalize data rows (not storeObject)
      await db
        .table(store)
        .toCollection()
        .modify((obj) => {
          if (obj?.id === store) return; // never treat storeObject as data row
          const coerced = coerceData(obj);
          const transformed = applyTransform(coerced, transform, {
            store,
            stepVersion: cfg.version,
            model: model.dataObject,
            utils,
          });

          // prune THEN assign, so removed fields actually disappear
          if (model.dataObject && typeof transform === "function") {
            keyPruning(obj, model.dataObject);
          }
          Object.assign(obj, transformed);
        });

      //previous
      // // if (model.dataObject) {
      //   await db
      //     .table(store)
      //     .toCollection()
      //     .modify((obj) => {
      //       if (obj?.id === store) return;
      //       const coerced = coerceData(obj);
      //       const transformed = applyTransform(coerced, transform, {
      //         store,
      //         stepVersion: cfg.version,
      //         model: model.dataObject,
      //         utils,
      //       });
      //       Object.assign(obj, transformed);
      //     });
      // }

      // StoreObject finalization ONLY if we’re in a MAJOR/MINOR step
      if (model.storeObject && allowStoreModelChange) {
        const meta = await db.table(store).get(store);
        if (meta) {
          const coercedMeta = coerceStore(meta);
          keyPruning(coercedMeta, model.storeObject); // drop removed keys
          coercedMeta.version = chosenVersion;
          coercedMeta.modified = Date.now();
          await db.table(store).put(coercedMeta);
        }
      } else {
        // still stamp version on populate
        const meta = await db.table(store).get(store);
        if (meta) {
          meta.version = chosenVersion;
          meta.modified = Date.now();
          await db.table(store).put(meta);
        }
      }

      if (typeof hooks.afterPopulate === "function") {
        await hooks.afterPopulate({ db, store, version: chosenVersion, cfg });
      }

      return; // done populate
    }

    // ---------- 2) MODEL CHANGES ON EXISTING STORE ----------
    // (a) Data rows: coerce + optional transform (skip storeObject)
    await db
      .table(store)
      .toCollection()
      .modify((obj) => {
        if (obj?.id === store) return; // skip storeObject
        const coerced = coerceData(obj);
        const transformed = applyTransform(coerced, transform, {
          store,
          stepVersion: cfg.version,
          model: model.dataObject,
          utils,
        });

        // only prune when a transform hook is present in this config
        if (model.dataObject && typeof transform === "function") {
          keyPruning(obj, model.dataObject);
        }
        Object.assign(obj, transformed);
      });

    //previous
    // if (model.dataObject) {
    //   await db
    //     .table(store)
    //     .toCollection()
    //     .modify((obj) => {
    //       if (obj?.id === store) return;
    //       const coerced = coerceData(obj);
    //       const transformed = applyTransform(coerced, transform, {
    //         store,
    //         stepVersion: cfg.version,
    //         model: model.dataObject,
    //         utils,
    //       });
    //       Object.assign(obj, transformed);
    //     });
    // }

    // (b) StoreObject row: ONLY coerce/prune on MAJOR/MINOR
    if (model.storeObject && allowStoreModelChange) {
      const meta = await db.table(store).get(store);
      if (meta) {
        const coercedMeta = coerceStore(meta);
        keyPruning(coercedMeta, model.storeObject);
        coercedMeta.version = chosenVersion;
        coercedMeta.modified = Date.now();
        await db.table(store).put(coercedMeta);
      }
    }

    // ---------- 3) PATCH: upsert provided records ----------
    if (Array.isArray(cfg.data) && cfg.data.length) {
      for (const patch of cfg.data) {
        if (!patch?.id) continue;

        // SPECIAL CASE: patch for storeObject (id === store)
        if (patch.id === store) {
          const existingMeta = await db.table(store).get(store);
          const mergedMeta = { ...(existingMeta || {}), ...patch };

          if (allowStoreModelChange && model.storeObject) {
            // MAJOR/MINOR: respect model change
            const coercedMeta = coerceStore(mergedMeta);
            keyPruning(coercedMeta, model.storeObject);
            coercedMeta.modified = Date.now();
            await db.table(store).put(coercedMeta);
          } else {
            // PATCH-only: merge values, no model change
            mergedMeta.modified = Date.now();
            await db.table(store).put(mergedMeta);
          }
          continue;
        }

        // Normal data record
        const existing = await db.table(store).get(patch.id);
        if (existing) {
          const merged = { ...existing, ...patch };
          const coerced = coerceData(merged);
          const transformed = applyTransform(coerced, transform, {
            store,
            stepVersion: cfg.version,
            model: model.dataObject,
            utils,
          });

          // Dexie put() replaces the object, but we still prune the payload explicitly
          const pruned = { ...transformed };
          if (model.dataObject && typeof transform === "function") {
            keyPruning(pruned, model.dataObject);
          }
          await db.table(store).put(pruned);
        } else {
          const seeded = model.dataObject
            ? createEntity(model.dataObject, patch, { required: ["id"] })
            : patch;
          const transformed = applyTransform(seeded, transform, {
            store,
            stepVersion: cfg.version,
            model: model.dataObject,
            utils,
          });
          // First insert may also prune if you asked for it in this version
          const pruned = { ...transformed };
          if (model.dataObject && typeof transform === "function") {
            keyPruning(pruned, model.dataObject);
          }
          await db.table(store).put(pruned);
        }

        //previous
        // if (existing) {
        //   const merged = { ...existing, ...patch };
        //   const coerced = coerceData(merged);
        //   const transformed = applyTransform(coerced, transform, {
        //     store,
        //     stepVersion: cfg.version,
        //     model: model.dataObject,
        //     utils,
        //   });
        //   await db.table(store).put(transformed);
        // } else {
        //   const seeded = model.dataObject
        //     ? createEntity(model.dataObject, patch, { required: ["id"] })
        //     : patch;
        //   const transformed = applyTransform(seeded, transform, {
        //     store,
        //     stepVersion: cfg.version,
        //     model: model.dataObject,
        //     utils,
        //   });
        //   await db.table(store).put(transformed);
        // }
      }
    }

    // ---------- 4) Final stamp on storeObject version ----------
    // Always stamp the store's version (even for PATCH) — this reflects the applied step
    const meta = await db.table(store).get(store);
    if (meta) {
      meta.version = chosenVersion;
      meta.modified = Date.now();
      await db.table(store).put(meta);
    }
  });
}

//previous
// // async function applyConfigStep(db, store, cfg, chosenVersion) {
//   const model =
//     cfg.model && (cfg.model.storeObject || cfg.model.dataObject)
//       ? cfg.model
//       : {};

//   const hooks = cfg.hooks || {};
//   const transform = hooks.transform; // single, generic hook
//   const utils = { keyPruning }; // utilities exposed to the hook

//   // Helpers
//   const coerceData = (obj) =>
//     model.dataObject
//       ? coerceToModel(model.dataObject, obj, { dropUnknown: false })
//       : obj;

//   const coerceStore = (obj) =>
//     model.storeObject
//       ? coerceToModel(model.storeObject, obj, { dropUnknown: false })
//       : obj;

//   await db.transaction("rw", db.table(store), async () => {
//     const count = await db.table(store).count();

//     // ---------- 1) POPULATE ----------
//     if (count === 0 && Array.isArray(cfg.data) && cfg.data.length) {
//       if (typeof hooks.beforePopulate === "function") {
//         await hooks.beforePopulate({ db, store, version: chosenVersion, cfg });
//       }

//       // Insert storeObject first (meta row, id === store)
//       if (model.storeObject) {
//         const metaId = store;
//         const provided = cfg.data.find((d) => d.id === metaId) || {
//           id: metaId,
//           label: store,
//           version: chosenVersion,
//           created: Date.now(),
//           modified: Date.now(),
//         };
//         const storeObj = createEntity(model.storeObject, provided, {
//           required: ["id"],
//         });
//         await db.table(store).put(storeObj);
//       }

//       // Insert data rows (skip storeObject id)
//       for (const rec of cfg.data) {
//         if (rec.id === store) continue;
//         const seeded = model.dataObject
//           ? createEntity(model.dataObject, rec, { required: ["id"] })
//           : rec;

//         // hook runs only for data rows
//         const shaped = applyTransform(seeded, transform, {
//           store,
//           stepVersion: cfg.version,
//           model: model.dataObject,
//           utils,
//         });

//         await db.table(store).put(shaped);
//       }

//       // Post-populate pass: coerce (again) + allow transform for *data rows only*
//       if (model.dataObject) {
//         await db
//           .table(store)
//           .toCollection()
//           .modify((obj) => {
//             if (obj?.id === store) return; // never treat storeObject as data row
//             const coerced = coerceData(obj);
//             const transformed = applyTransform(coerced, transform, {
//               store,
//               stepVersion: cfg.version,
//               model: model.dataObject,
//               utils,
//             });
//             Object.assign(obj, transformed);
//           });
//       }

//       // Always coerce/prune the storeObject itself if model changed
//       if (model.storeObject) {
//         const meta = await db.table(store).get(store);
//         if (meta) {
//           const coercedMeta = coerceStore(meta);
//           // If the storeObject model removed keys, drop them:
//           keyPruning(coercedMeta, model.storeObject);
//           coercedMeta.version = chosenVersion;
//           coercedMeta.modified = Date.now();
//           await db.table(store).put(coercedMeta);
//         }
//       }

//       if (typeof hooks.afterPopulate === "function") {
//         await hooks.afterPopulate({ db, store, version: chosenVersion, cfg });
//       }

//       return; // done populate
//     }

//     // ---------- 2) MODEL CHANGES ON EXISTING STORE ----------
//     // a) Data rows: coerce + optional transform (skip storeObject)
//     if (model.dataObject) {
//       await db
//         .table(store)
//         .toCollection()
//         .modify((obj) => {
//           if (obj?.id === store) return; // never apply data model to storeObject
//           const coerced = coerceData(obj);
//           const transformed = applyTransform(coerced, transform, {
//             store,
//             stepVersion: cfg.version,
//             model: model.dataObject,
//             utils,
//           });
//           Object.assign(obj, transformed);
//         });
//     }

//     // b) StoreObject row: always coerce to storeObject model and prune removed keys
//     if (model.storeObject) {
//       const meta = await db.table(store).get(store);
//       if (meta) {
//         const coercedMeta = coerceStore(meta);
//         keyPruning(coercedMeta, model.storeObject); // drop removed keys
//         coercedMeta.version = chosenVersion;
//         coercedMeta.modified = Date.now();
//         await db.table(store).put(coercedMeta);
//       }
//     }

//     // ---------- 3) PATCH: upsert provided records ----------
//     if (Array.isArray(cfg.data) && cfg.data.length) {
//       for (const patch of cfg.data) {
//         if (!patch?.id) continue;

//         // SPECIAL CASE: patch for storeObject (id === store)
//         if (patch.id === store) {
//           const existingMeta = await db.table(store).get(store);
//           const mergedMeta = { ...(existingMeta || {}), ...patch };
//           const coercedMeta = coerceStore(mergedMeta);
//           keyPruning(coercedMeta, model.storeObject || coercedMeta); // in case model removed keys
//           coercedMeta.modified = Date.now();
//           await db.table(store).put(coercedMeta);
//           continue;
//         }

//         // Normal data record
//         const existing = await db.table(store).get(patch.id);
//         if (existing) {
//           const merged = { ...existing, ...patch };
//           const coerced = coerceData(merged);
//           const transformed = applyTransform(coerced, transform, {
//             store,
//             stepVersion: cfg.version,
//             model: model.dataObject,
//             utils,
//           });
//           await db.table(store).put(transformed);
//         } else {
//           const seeded = model.dataObject
//             ? createEntity(model.dataObject, patch, { required: ["id"] })
//             : patch;
//           const transformed = applyTransform(seeded, transform, {
//             store,
//             stepVersion: cfg.version,
//             model: model.dataObject,
//             utils,
//           });
//           await db.table(store).put(transformed);
//         }
//       }
//     }

//     // ---------- 4) Final stamp on storeObject ----------
//     if (model.storeObject) {
//       const meta = await db.table(store).get(store);
//       if (meta) {
//         meta.version = chosenVersion;
//         meta.modified = Date.now();
//         await db.table(store).put(meta);
//       }
//     }
//   });
// }

// previous
// async function applyConfigStep(db, store, cfg, chosenVersion) {
//   const model =
//     cfg.model && (cfg.model.storeObject || cfg.model.dataObject)
//       ? cfg.model
//       : {};

//   await db.transaction("rw", db.table(store), async () => {
//     const count = await db.table(store).count();

//     // 1) POPULATE: brand-new store
//     if (count === 0 && Array.isArray(cfg.data) && cfg.data.length) {
//       // debug to check for run on db initialize
//       console.debug(
//         `[${store}] POPULATE: inserting ${
//           cfg.data?.length || 0
//         } rows @ ${chosenVersion}`
//       );

//       // storeObject first (if provided), use provided seed or synthesize a minimal one
//       if (model.storeObject) {
//         const metaId = store;
//         const provided = cfg.data.find((d) => d.id === metaId) || {
//           id: metaId,
//           label: store,
//           version: chosenVersion,
//           created: Date.now(),
//           modified: Date.now(),
//         };
//         const storeObj = createEntity(model.storeObject, provided, {
//           required: ["id"],
//         });
//         await db.table(store).put(storeObj);
//       }

//       // seed data records
//       for (const rec of cfg.data) {
//         if (rec.id === store) continue; // skip the storeObject; already handled
//         const finalRec = model.dataObject
//           ? createEntity(model.dataObject, rec, { required: ["id"] })
//           : rec;
//         await db.table(store).put(finalRec);
//       }

//       // safety re-coercion to ensure shape is fully applied on all rows
//       if (model.dataObject) {
//         await db
//           .table(store)
//           .toCollection()
//           .modify((obj) => {
//             Object.assign(obj, coerceToModel(model.dataObject, obj));
//           });
//       }

//       // Stamp storeObject.version inside the store (if present)
//       if (model.storeObject) {
//         const meta = await db.table(store).get(store);
//         if (meta) {
//           meta.version = chosenVersion;
//           meta.modified = new Date();
//           await db.table(store).put(meta);
//         }
//       }

//       return; // populate complete
//     }

//     // 2) MINOR: coerce all rows to the latest model
//     if (model.dataObject) {
//       await db
//         .table(store)
//         .toCollection()
//         .modify((obj) => {
//           Object.assign(obj, coerceToModel(model.dataObject, obj));
//         });
//     }

//     // 3) PATCH: upsert provided records
//     if (Array.isArray(cfg.data) && cfg.data.length) {
//       for (const patch of cfg.data) {
//         if (!patch?.id) continue;
//         const existing = await db.table(store).get(patch.id);
//         if (existing) {
//           const merged = { ...existing, ...patch };
//           const shaped = model.dataObject
//             ? coerceToModel(model.dataObject, merged)
//             : merged;
//           await db.table(store).put(shaped);
//         } else {
//           const shaped = model.dataObject
//             ? createEntity(model.dataObject, patch, { required: ["id"] })
//             : patch;
//           await db.table(store).put(shaped);
//         }
//       }
//     }

//     // Update storeObject.version if present
//     if (model.storeObject) {
//       const meta = await db.table(store).get(store);
//       if (meta) {
//         meta.version = chosenVersion;
//         meta.modified = new Date();
//         await db.table(store).put(meta);
//       }
//     }
//   });
// }

// previous
// async function applyConfigStep(db, store, cfg, models, chosenVersion) {
//   const model =
//     cfg.model && (cfg.model.storeObject || cfg.model.dataObject)
//       ? cfg.model
//       : models[store] || {};

//   // Populate path: brand-new store + config has data
//   const count = await db.table(store).count();
//   let curId = 0;
//   console.log(cfg);
//   if (count === 0 && cfg.data && cfg.data.length) {
//     /**
//      * Initialize or update a store's metadata object in the database.
//      *
//      * This code checks if the model defines a storeObject schema, and if so:
//      * 1. Creates or retrieves metadata for the store itself
//      * 2. Validates and creates an entity based on the storeObject schema
//      * 3. Persists the metadata object to the store's table
//      *
//      * Convention: The storeObject's id matches the store name, allowing each
//      * store to contain a self-describing metadata record.
//      */
//     if (model && model.storeObject) {
//       const metaId = store; // convention: storeObject.id === store name
//       const provided = cfg.data.find((d) => d.id === metaId) || {
//         id: metaId,
//         label: store,
//         version: chosenVersion, //cfg.version,
//       };
//       const storeObj = createEntity(model.storeObject, provided, {
//         required: ["id"],
//       });
//       console.debug(storeObj);
//       await db.table(store).put(storeObj);
//     }

//     // populate loop
//     for (const rec of cfg.data) {
//       if (rec.id === store) continue; // meta handled above
//       const finalRec = model?.dataObject
//         ? createEntity(model.dataObject, rec, { required: ["id"] })
//         : rec;
//       console.debug(finalRec);
//       await db.table(store).put(finalRec);

//       const cleanRec = JSON.parse(JSON.stringify(finalRec));
//       await db.table(store).put(cleanRec);
//       const roundTrip = await db.table(store).get(cleanRec.id);
//       console.debug("[roundTrip]", cleanRec.id, roundTrip);

//       //   // Read it back immediately
//       //   const roundTrip = await db.table(store).get(finalRec.id);

//       //   curId = finalRec.id;
//       //   //debug
//       //   console.log("[roundTrip]", finalRec.id, JSON.stringify(roundTrip));
//     }

//     return;
//   }

//   // MINOR: coerce all existing records to new template (adds new fields, preserves unknown by default)
//   if (model && model.dataObject) {
//     await db
//       .table(store)
//       .toCollection()
//       .modify((obj) => {
//         const next = coerceToModel(model.dataObject, obj);
//         Object.assign(obj, next);
//       });

//     console.debug("minor");
//     const minorObj = await db.table(store).get("theme-accent");

//     console.debug("[minor]", curId, JSON.stringify(minorObj));
//   }

//   // PATCH: shallow upsert of provided records
//   if (Array.isArray(cfg.data) && cfg.data.length) {
//     for (const patch of cfg.data) {
//       const id = patch.id;
//       if (!id) continue;
//       const existing = await db.table(store).get(id);

//       if (existing) {
//         const merged = { ...existing, ...patch };
//         const toWrite = model?.dataObject
//           ? coerceToModel(model.dataObject, merged)
//           : merged;

//         await db.table(store).put(toWrite);

//         // 🔍 TEMP: read back exactly what Dexie stored
//         // const roundTrip = await db.table(store).get(id);
//         // console.debug(
//         //   `[patch roundTrip:existing] ${store}/${id}`,
//         //   { wroteKeys: Object.keys(toWrite), readKeys: Object.keys(roundTrip) },
//         //   roundTrip
//         // );
//       } else {
//         const toWrite = model?.dataObject
//           ? createEntity(model.dataObject, patch, { required: ["id"] })
//           : patch;

//         await db.table(store).put(toWrite);

//         // 🔍 TEMP: read back exactly what Dexie stored
//         // const roundTrip = await db.table(store).get(id);
//         // console.debug(
//         //   `[patch roundTrip:new] ${store}/${id}`,
//         //   { wroteKeys: Object.keys(toWrite), readKeys: Object.keys(roundTrip) },
//         //   roundTrip
//         // );
//       }
//       //previous
//       //   if (existing) {
//       //     const merged = { ...existing, ...patch };
//       //     await db
//       //       .table(store)
//       //       .put(
//       //         model?.dataObject ? coerceToModel(model.dataObject, merged) : merged
//       //       );
//       //   } else {
//       //     await db
//       //       .table(store)
//       //       .put(
//       //         model?.dataObject
//       //           ? createEntity(model.dataObject, patch, { required: ["id"] })
//       //           : patch
//       //       );
//       //   }
//     }
//   }

//   // Update storeObject.version if present
//   if (model && model.storeObject) {
//     const metaId = store;
//     const meta = await db.table(store).get(metaId);
//     if (meta) {
//       meta.version = chosenVersion; //cfg.version;
//       meta.modified = Date.now();
//       await db.table(store).put(meta);
//     }
//   }
// }

/**
 * Aggregates model templates across a timeline for a store (latest wins).
 *
 * @param {Array<{model?:{storeObject?:Object, dataObject?:Object}}>} timeline
 * @param {string} store
 * @returns {{[store:string]:{storeObject?:Object, dataObject?:Object}}}
 */
function collectModelsForStore(timeline, store) {
  const out = {};
  //   const out = { ...(defaultModels[store] || {}) };
  for (const cfg of timeline) {
    if (!cfg.model) continue;
    if (cfg.model.storeObject) out.storeObject = cfg.model.storeObject;
    if (cfg.model.dataObject) out.dataObject = cfg.model.dataObject;
  }

  return out;
}

/**
 * FNV-1a style tiny hash → positive 32-bit number, reduced to a safe Dexie version int.
 * Used to deterministically derive a single Dexie version from the store definitions string.
 *
 * @param {string} s - Input string to hash (e.g., concatenated store defs).
 * @returns {number} Deterministic non-negative integer < 2,147,483,000
 */
// function hashToInt(s) {
//   // FNV-1a 32-bit
//   let h = 2166136261 >>> 0;
//   for (let i = 0; i < s.length; i++) {
//     h ^= s.charCodeAt(i);
//     h = Math.imul(h, 16777619);
//   }
//   const v = (h >>> 0) % 2147483000;
//   return v === 0 ? 1 : v;
// }

// previous
// function hashToInt(s) {
//   let h = 2166136261 >>> 0; // FNV-1a seed
//   for (let i = 0; i < s.length; i++) {
//     h ^= s.charCodeAt(i);
//     h = Math.imul(h, 16777619);
//   }
//   return (h >>> 0) % 2147483000;
// }

// small helpers to enforce your version policy.

/**
 * Return the allowed next semantic-version strings for a given triplet.
 *
 * Given a version string "A.B.C", this function produces a Set of three
 * candidate next versions according to these rules:
 *  - patch bump:      "A.B.(C+1)"
 *  - minor bump:      "A.(B+1).1" (minor increment, patch reset to 1)
 *  - major bump:      "(A+1).1.1" (major increment, minor/patch reset to 1)
 *
 * @param {string} prev - Previous version in "major.minor.patch" format.
 * @returns {Set<string>} A set of allowed next-version strings.
 *
 * @example
 * // from "1.2.3" => Set { "1.2.4", "1.3.1", "2.1.1" }
 */
function allowedNextVersions(prev) {
  const { major: A, minor: B, patch: C } = parseTriplet(prev);
  return new Set([`${A}.${B}.${C + 1}`, `${A}.${B + 1}.1`, `${A + 1}.1.1`]);
}

/**
 * decideNextVersion({ prev, store, cfg })
 * ---------------------------------------
 * Implements your rules for *whether* to migrate and *which* version to use.
 * - Reject if file/object versions missing or invalid
 * - Reject if both < prev
 * - When mismatch, select the one that is a valid next; warn if mismatch
 * - When match, ensure it's a valid next
 *
 * Returns: { ok, chosen, warnMsg?, errMsg? }
 */
function decideNextVersion({ prev, store, cfg }) {
  const objectV = cfg.version; // required in config object
  const fileV = cfg.fileVersion || extractVersionFromFilename(cfg.filePath); // may be null
  const hasObj = !!objectV;
  const hasFile = !!fileV;
  const objValid = hasObj && isValidTriplet(objectV);
  const fileValid = hasFile && isValidTriplet(fileV);

  // Fail fast: missing/invalid formats
  if (!hasObj || !objValid) {
    return {
      ok: false,
      errMsg: `[${store}] migration blocked: config object version missing/invalid (${
        objectV ?? "missing"
      }).`,
    };
  }
  if (!hasFile || !fileValid) {
    return {
      ok: false,
      errMsg: `[${store}] migration blocked: filename version missing/invalid (${
        fileV ?? "missing"
      }).`,
    };
  }

  // Both present & valid → reject if both < prev
  const bothLessThanPrev =
    cmpTriplet(objectV, prev) <= 0 && cmpTriplet(fileV, prev) <= 0;
  if (bothLessThanPrev) {
    return {
      ok: false,
      errMsg: `[${store}] migration blocked: both file (${fileV}) and object (${objectV}) versions are not greater than previous (${prev}).`,
    };
  }

  const allowed = allowedNextVersions(prev);

  // If both match each other
  if (objectV === fileV) {
    if (!allowed.has(objectV)) {
      return {
        ok: false,
        errMsg: `[${store}] migration blocked: version ${objectV} is not a valid next from ${prev} (allowed: ${[
          ...allowed,
        ].join(", ")}).`,
      };
    }
    return { ok: true, chosen: objectV }; // step 2: choose object version
  }

  // They differ → choose the one that is a valid next (if any)
  const objAllowed = allowed.has(objectV) && cmpTriplet(objectV, prev) > 0;
  const fileAllowed = allowed.has(fileV) && cmpTriplet(fileV, prev) > 0;

  if (!objAllowed && !fileAllowed) {
    return {
      ok: false,
      errMsg: `[${store}] migration blocked: neither file (${fileV}) nor object (${objectV}) is a valid next from ${prev} (allowed: ${[
        ...allowed,
      ].join(", ")}).`,
    };
  }

  // Prefer the one that is valid; warn about mismatch
  const chosen = objAllowed ? objectV : fileV;
  const other = objAllowed ? fileV : objectV;
  return {
    ok: true,
    chosen,
    warnMsg: `[${store}] version mismatch: using ${chosen} as next from ${prev}; other candidate ${other} ignored.`,
  };
}

/**
 * shouldPrompt(bumpType, level)
 * level: "major" | "minor" | "patch"
 * - "major": prompt only for major
 * - "minor": prompt for major or minor
 * - "patch": prompt for all
 */
// function shouldPrompt(bumpType, level) {
//   if (level === "patch") return true;
//   if (level === "minor") return bumpType === "major" || bumpType === "minor";
//   return bumpType === "major"; // level === "major"
// }

/**
 * planAndMigrate(db, configs, options?)
 * -------------------------------------
 * Adds:
 *  - __history recording for "blocked" | "skipped" | "migrate"
 *  - user prompt gate for Major/Minor/Patch updates (single modal for all pending)
 *
 * @param {Dexie} db
 * @param {Array}  configs                 normalized configs
 * @param {Object} [options]
 * @param {"major"|"minor"|"patch"} [options.updatePromptLevel="major"]
 *        - "major": prompt only for MAJOR
 *        - "minor": prompt for MAJOR or MINOR
 *        - "patch": prompt for MAJOR, MINOR, and PATCH
 * @param {function} [options.promptUser]  async (pendingSummary[]) => boolean
 *        - pendingSummary: { store, from, to, bumpType, filePath, fileVersion, objectVersion, warning? }
 *        - returns true to proceed, false to cancel (and record "skipped")
 */
export async function planAndMigrate(db, configs, options = {}) {
  // console.debug("planAndMigrate");

  const {
    updatePromptLevel = "patch",
    promptUser = null, // may be undefined
  } = options;

  // ---------- helpers ----------
  // returns a stable signature for a single store's index definition
  function storeSignature(def) {
    // keep it simple: the Dexie index string as-is
    return String(def || "");
  }

  const shouldPrompt = (bumpType, level) => {
    if (level === "patch") return true; // prompt for all
    if (level === "minor") return bumpType === "major" || bumpType === "minor";
    return bumpType === "major"; // default: major only
  };

  const hasHistoryStore = (storesObj) =>
    typeof storesObj["__history"] === "string";

  // ---------- compute stores + (re)open DB ----------
  /**
   * buildDexieStoresFromConfigs returns:
   *
   * settings: "id,modified,category"
   * __history: "id,store,applied,newVersion,prevVersion"
   * __schema: "id"
   */
  const stores = buildDexieStoresFromConfigs(configs);

  // Ensure DB is at least open with whatever schema exists so we can read __schema, etc.
  if (!db.isOpen()) {
    try {
      await db.open();
    } catch (_) {
      // On brand-new DB, open() may fail before any version is defined.
      // That's OK — we'll create schema later when needSchemaUpgrade is true.
    }
  }

  // const versionSeed = Object.entries(stores)
  //   .map(([name, def]) => `${name}=${def}`) // include META too; keeps version hash stable
  //   .sort()
  //   .join("|");

  // const dexieVersion = hashToInt(versionSeed);

  // simple monotonic counter based on current verno
  // const nextVersion = (db.verno || 0) + 1;

  // if (db.isOpen()) await db.close();
  // db.version(nextVersion).stores(stores);

  // // db.version(dexieVersion).stores(stores);
  // await db.open();

  // ---------- fresh install? (pure read; NO writes here) ----------
  const freshInstall = await (async () => {
    try {
      // if __schema table opens and is empty -> fresh
      const t = db.table("__schema");
      const c = await t.count();
      return c === 0;
    } catch {
      // very first open, table not fully ready yet: treat as fresh
      return true;
    }
  })();
  // console.debug(`[schema] freshInstall=${freshInstall}`);

  // ---------- Phase 1: PLAN ----------
  const perStore = new Set(Object.keys(stores)); // include __schema & __history in planning
  const pending = []; // { store, prevVersion, nextVersion, bumpType, step, decision }

  for (const store of perStore) {
    const timeline = getStoreTimeline(configs, store);
    if (timeline.length === 0) continue;

    let prev = await getPrevVersion(db, store);
    const allowed = allowedNextVersions(prev);
    // console.debug(
    //   `[${store}] prev=${prev}, allowed next: ${[...allowed].join(", ")}`
    // );

    for (const step of timeline.sort((a, b) =>
      cmpTriplet(a.version, b.version)
    )) {
      const decision = decideNextVersion({ prev, store, cfg: step });
      // Debug current step + decision
      // console.debug(`[${store}] step=${step.version} -> decision:`, decision);

      //  skip benign no-ops (same or older) without history
      if (!decision.ok) {
        const isSameOrOlder = cmpTriplet(step.version, prev) <= 0; // ★
        if (isSameOrOlder) {
          // console.debug(
          //   `[${store}] skip same or older: file version ${step.version} <= prev ${prev}` // ★
          // );
          // no history row for this benign case                       // ★
          continue; // ★
        }

        // real “blocked” (format/policy/mismatch) -> record once
        // console.error(decision.errMsg);
        if (typeof stores["__history"] === "string") {
          // ★ guard
          await appendHistory(db, {
            store,
            prevVersion: prev,
            newVersion: step.version,
            bumpType: null,
            applied: Date.now(),
            stepFile: step.filePath,
            fileVersion: step.fileVersion || null,
            objectVersion: step.version,
            decision: "blocked",
            reason: decision.errMsg,
            changeLog: "none",
          });
        }
        continue;
      }

      // Only queue steps that actually advance the version
      if (cmpTriplet(decision.chosen, prev) > 0) {
        const bumpType = deriveBumpType(prev, decision.chosen);
        pending.push({
          store,
          prevVersion: prev,
          nextVersion: decision.chosen,
          bumpType,
          step,
          decision,
        });
        prev = decision.chosen; // advance baseline within this planning pass
      } else {
        // console.debug(
        //   `[${store}] skip step ${step.version} (chosen ${decision.chosen} <= prev ${prev})`
        // );
      }
    }
  }

  // 5) Decide if an IDB schema upgrade is required (MAJOR + signature mismatch or unknown)
  let needSchemaUpgrade = false;
  for (const u of pending) {
    if (u.bumpType !== "major") continue;

    // incoming signature from the FINAL stores map
    const incomingDef = stores[u.store];
    const incomingSig = storeSignature(incomingDef);

    // previous signature from __schema row (if any)
    let prevSig = null;
    try {
      const row = await db.table("__schema").get(u.store);
      prevSig = row?.schemaSignature || null;
    } catch (_) {}

    // if we cannot read previous signature OR it differs → we upgrade
    if (!prevSig || prevSig !== incomingSig) {
      needSchemaUpgrade = true;
      break;
    }
  }

  // 6) If schema upgrade needed, bump verno and reopen *before* prompt/apply
  if (needSchemaUpgrade) {
    // use 0 as the fallback, so first bump(db init) is 1, not 2
    const nextV = (db.verno || 0) + 1;
    await db.close();
    db.version(nextV).stores(stores);
    await db.open();
    // console.debug(
    //   `[schema] IDB upgrade applied (verno ${nextV}) for MAJOR schema changes`
    // );
  }

  // If nothing to do → never prompt, return immediately
  if (pending.length === 0) {
    // console.debug("[schema] no pending updates. skipping prompt.");
    return db;
  }

  // ---------- Phase 2: PROMPT (only when not fresh & policy requires) ----------
  // [ ] change forceUpdate behavior when backup functionality is added for user prompt
  const hasForceUpdate = pending.some((u) => u.step?.forceUpdate === true);
  const needsPrompt =
    !freshInstall &&
    !hasForceUpdate &&
    pending.some((u) => shouldPrompt(u.bumpType, updatePromptLevel));
  // console.debug(
  //   `[schema] needsPrompt=${needsPrompt}, pending=${pending.length}, policy=${updatePromptLevel}`
  // );

  if (needsPrompt && typeof promptUser === "function") {
    const summary = pending.map((u) => ({
      store: u.store,
      from: u.prevVersion,
      to: u.nextVersion,
      bumpType: u.bumpType,
      filePath: u.step.filePath,
      fileVersion: u.step.fileVersion || null,
      objectVersion: u.step.version,
      warning: u.decision.warnMsg || null,
      changeDescription: u.step.changeDescription,
    }));

    const proceed = await promptUser(summary);
    if (!proceed) {
      if (hasHistoryStore(stores)) {
        for (const u of pending) {
          await appendHistory(db, {
            store: u.store,
            prevVersion: u.prevVersion,
            newVersion: u.nextVersion,
            bumpType: u.bumpType,
            applied: Date.now(),
            stepFile: u.step.filePath,
            fileVersion: u.step.fileVersion || null,
            objectVersion: u.step.version,
            decision: "skipped",
            reason: "user-declined",
            changeLog: "none",
          });
        }
      }
      console.warn(
        "[schema] user declined updates; returning without applying."
      );
      return db;
    }
  }

  // ---------- Phase 3: APPLY ----------
  for (const u of pending) {
    const { store, prevVersion, nextVersion, step, decision, bumpType } = u;

    if (decision.warnMsg) console.warn(decision.warnMsg);

    try {
      // await applyConfigStep(db, store, step, nextVersion);
      await applyConfigStep(db, store, step, nextVersion, { bumpType });
      const sig =
        bumpType === "major" ? storeSignature(stores[store]) : undefined;
      await setVersion(db, store, nextVersion, sig);
      // Persist chosen version into __schema and store meta

      // use setVersion
      // await (async function setVersionLocal(db, store, version, schemaSig) {
      //   await db.table("__schema").put(
      //     {
      //       id: store,
      //       version,
      //       schemaSignature: schemaSig ?? "",
      //       modified: Date.now(),
      //     },
      //     store
      //   );
      //   try {
      //     const meta = await db.table(store).get(store);
      //     if (meta) {
      //       meta.version = version;
      //       meta.modified = Date.now();
      //       await db.table(store).put(meta);
      //     }
      //   } catch (_) {}
      // })(db, store, nextVersion);

      if (hasHistoryStore(stores)) {
        await appendHistory(db, {
          store,
          prevVersion,
          newVersion: nextVersion,
          bumpType,
          applied: Date.now(),
          stepFile: step.filePath,
          fileVersion: step.fileVersion || null,
          objectVersion: step.version,
          decision: "migrate",
          changeLog: step.changeLog,
        });
      }
    } catch (err) {
      console.error(`[${store}] migration failed:`, err);
      if (hasHistoryStore(stores)) {
        await appendHistory(db, {
          store,
          prevVersion,
          newVersion: nextVersion,
          bumpType,
          applied: Date.now(),
          stepFile: step.filePath,
          fileVersion: step.fileVersion || null,
          objectVersion: step.version,
          decision: "blocked",
          reason: String(err?.message || err),
          changeLog: "none",
        });
      }
      // continue with the rest
    }
  }

  return db;
}

// previous
// export async function planAndMigrate(db, configs, options = {}) {
//   const {
//     updatePromptLevel = "major",
//     promptUser = null, // if not provided, auto-approve without prompting
//   } = options;

//   // ---- helpers (local) ----

//   // REDEFINED BELOW
//   // ADD: detect fresh install (no recorded versions yet)
//   // async function isFreshInstall(db) {
//   //   try {
//   //     const metaTable = db.table(META); // "__schema"
//   //     // If __schema exists but has 0 rows, treat as fresh
//   //     const metaCount = await metaTable.count();
//   //     return metaCount === 0;
//   //   } catch (_) {
//   //     // If table isn't available yet right after open(), also treat as fresh
//   //     return true;
//   //   }
//   // }

//   function shouldPrompt(bumpType, level) {
//     if (level === "patch") return true; // prompt for all
//     if (level === "minor") return bumpType === "major" || bumpType === "minor";
//     return bumpType === "major"; // default: major only
//   }

//   // ---- build stores + (re)open DB (unchanged) ----
//   const stores = buildDexieStoresFromConfigs(configs);

//   const versionSeed = Object.entries(stores)
//     .filter(([name]) => name !== META)
//     .map(([name, def]) => name + "=" + def)
//     .sort()
//     .join("|");
//   const dexieVersion = hashToInt(versionSeed);

//   // Ensure schema is defined before open; close if already open
//   if (db.isOpen()) {
//     await db.close();
//   }
//   db.version(dexieVersion).stores(stores);
//   await db.open();

//   // const freshInstall = await isFreshInstall(db);
//   // --- NEW: detect fresh install (no rows in __schema) ---------------------
//   const freshInstall = await (async () => {
//     try {
//       const t = db.table(META); // "__schema"
//       const c = await t.count();
//       return c === 0;
//     } catch {
//       return true;
//     }
//   })();
//   console.debug(`[schema] freshInstall=${freshInstall}`);

//   // [ ] commented end of line to process __schema
//   const perStore = new Set(Object.keys(stores)); //.filter((s) => s !== META));

//   console.debug(configs);
//   // ---- Phase 1: plan all pending updates and record "blocked" decisions in history ----
//   const pending = []; // { store, prevVersion, nextVersion, bumpType, step, decision }
//   for (const store of perStore) {
//     console.debug(store);
//     const timeline = getStoreTimeline(configs, store);
//     console.debug(timeline);
//     if (timeline.length === 0) continue;

//     let prev = await getPrevVersion(db, store);

//     // Debug allowed-next list
//     const allowed = allowedNextVersions(prev);
//     console.debug(
//       `[${store}] prev=${prev}, allowed next: ${[...allowed].join(", ")}`
//     );
//     // [__history] prev=0.0.0, allowed next: 0.0.1, 0.1.1, 1.1.1

//     for (const step of timeline.sort((a, b) =>
//       cmpTriplet(a.version, b.version)
//     )) {
//       const decision = decideNextVersion({ prev, store, cfg: step });
//       console.debug(decision);
//       if (!decision.ok) {
//         console.error(decision.errMsg);
//         // history: blocked (policy/format/etc.)
//         await appendHistory(db, {
//           store,
//           prevVersion: prev,
//           newVersion: step.version,
//           bumpType: null,
//           applied: Date.now(),
//           stepFile: step.filePath,
//           fileVersion: step.fileVersion || null,
//           objectVersion: step.version,
//           decision: "blocked",
//           reason: decision.errMsg,
//         });
//         continue;
//       }

//       // If chosen is newer than prev, stage it (do not apply yet)
//       if (cmpTriplet(decision.chosen, prev) > 0) {
//         const bumpType = deriveBumpType(prev, decision.chosen);
//         pending.push({
//           store,
//           prevVersion: prev,
//           nextVersion: decision.chosen,
//           bumpType,
//           step,
//           decision,
//         });
//         // advance the preview baseline for chaining multiple steps in one run
//         prev = decision.chosen;
//       }
//     }
//   }

//   // --- CRITICAL: if nothing is pending, DO NOT prompt; just return ---------- //
//   if (pending.length === 0) {
//     console.debug("[schema] no pending updates. skipping prompt.");
//     return db;
//   }

//   // ---- Phase 2: optionally prompt the user (single modal for all pending) ----
//   // ---  skip prompting entirely on fresh install -----------------------
//   const needsPrompt =
//     !freshInstall && pending.some((u) => shouldPrompt(u.bumpType));
//   console.debug(
//     `[schema] needsPrompt=${needsPrompt}, pending=${pending.length}`
//   );
//   // previous
//   // const needsPrompt =
//   //   !freshInstall &&
//   //   pending.some((u) => shouldPrompt(u.bumpType, updatePromptLevel));

//   if (needsPrompt && typeof promptUser === "function") {
//     const summary = pending.map((u) => ({
//       store: u.store,
//       from: u.prevVersion,
//       to: u.nextVersion,
//       bumpType: u.bumpType,
//       filePath: u.step.filePath,
//       fileVersion: u.step.fileVersion || null,
//       objectVersion: u.step.version,
//       warning: u.decision.warnMsg || null,
//     }));

//     const proceed = await promptUser(summary);
//     if (!proceed) {
//       // record "skipped" (user declined) for each staged update
//       for (const u of pending) {
//         await appendHistory(db, {
//           store: u.store,
//           prevVersion: u.prevVersion,
//           newVersion: u.nextVersion,
//           bumpType: u.bumpType,
//           applied: Date.now(),
//           stepFile: u.step.filePath,
//           fileVersion: u.step.fileVersion || null,
//           objectVersion: u.step.version,
//           decision: "skipped",
//           reason: "user-declined",
//         });
//       }
//       return db;
//     }
//   }

//   // ---- Phase 3: apply in order, record "migrate" or "blocked" (runtime error) ----
//   for (const u of pending) {
//     const { store, prevVersion, nextVersion, step, decision, bumpType } = u;

//     if (decision.warnMsg) {
//       console.warn(decision.warnMsg);
//     }

//     try {
//       // Apply step (uses chosen version for stamping inside storeObject)
//       await applyConfigStep(db, store, step, nextVersion);

//       //   // Persist chosen version into both the store's meta row and __schema
//       await setVersion(db, store, nextVersion);

//       //   await (async function setVersionLocal(db, store, version) {
//       //     await db
//       //       .table(META)
//       //       .put({ id: store, version, modified: new Date() }, store);
//       //     try {
//       //       const meta = await db.table(store).get(store);
//       //       if (meta) {
//       //         meta.version = version;
//       //         meta.modified = new Date();
//       //         await db.table(store).put(meta);
//       //       }
//       //     } catch (_) {}
//       //   })(db, store, nextVersion);

//       // history: migrate
//       await appendHistory(db, {
//         store,
//         prevVersion,
//         newVersion: nextVersion,
//         bumpType,
//         applied: Date.now(),
//         stepFile: step.filePath,
//         fileVersion: step.fileVersion || null,
//         objectVersion: step.version,
//         decision: "migrate",
//       });
//     } catch (err) {
//       console.error(`[${store}] migration failed:`, err);
//       // history: blocked due to runtime failure
//       await appendHistory(db, {
//         store,
//         prevVersion,
//         newVersion: nextVersion,
//         bumpType,
//         applied: Date.now(),
//         stepFile: step.filePath,
//         fileVersion: step.fileVersion || null,
//         objectVersion: step.version,
//         decision: "blocked",
//         reason: String(err?.message || err),
//       });
//       // continue to next pending update (do not abort other stores)
//     }
//   }

//   return db;
// }

// previous
// /**
//  * Main entry for planning and executing migrations.
//  * Steps:
//  *   1) Build combined Dexie store defs from all configs (latest MAJOR per store).
//  *   2) Hash the store-def string → a stable Dexie version number.
//  *   3) Open Dexie with computed stores + version.
//  *   4) For each store, walk its timeline from previous → latest and apply steps.
//  *
//  * @param {string} dbName - Name of the IndexedDB database.
//  * @param {Array<{version:string, stores:Object<string,string>, model?:Object, data?:Object[]}>} configs
//  * @returns {Promise<Dexie>} Open, migrated Dexie instance.
//  */
// // export async function planAndMigrate(dbName, configs) {
// export async function planAndMigrate(db, configs) {
//   const stores = buildDexieStoresFromConfigs(configs);

//   const versionSeed = Object.entries(stores)
//     .filter(([name]) => name !== META)
//     .map(([name, def]) => name + "=" + def)
//     .sort()
//     .join("|");
//   const dexieVersion = hashToInt(versionSeed);

//   //   const db = new Dexie(dbName);
//   //   db.version(dexieVersion).stores(stores);
//   //   await db.open();

//   // Ensure schema is defined before open; close if already open
//   if (db.isOpen()) {
//     await db.close();
//   }
//   db.version(dexieVersion).stores(stores);
//   await db.open();

//   const perStore = new Set(Object.keys(stores).filter((s) => s !== META));

//   for (const store of perStore) {
//     const timeline = getStoreTimeline(configs, store);
//     if (timeline.length === 0) continue;

//     let prev = await getPrevVersion(db, store);
//     // previous
//     // const prev = await (async function getPrevVersion(db, store) {
//     //   await db
//     //     .table(META)
//     //     .put({ id: store, version: "0.0.0", modified: new Date(0) }, store);
//     //   const rec = await db.table(META).get(store);
//     //   return rec?.version || "0.0.0";
//     // })(db, store);

//     // Debug: now shows the true previous version (e.g., 1.1.1 on subsequent boots)
//     const allowed = allowedNextVersions(prev);
//     console.debug(
//       `[${store}] prev=${prev}, allowed next: ${[...allowed].join(", ")}`
//     );

//     for (const step of timeline.sort((a, b) =>
//       cmpTriplet(a.version, b.version)
//     )) {
//       const decision = decideNextVersion({ prev, store, cfg: step });

//       if (!decision.ok) {
//         console.error(decision.errMsg);
//         continue; // skip this step
//       }
//       if (decision.warnMsg) {
//         console.warn(decision.warnMsg);
//       }

//       // Only apply if chosen > prev (already guaranteed by decideNextVersion)
//       await applyConfigStep(
//         db,
//         store,
//         step,
//         /* chosenVersion: */ decision.chosen
//       );

//       // Persist chosen version into both the store's meta row and __schema
//       await (async function setVersion(db, store, version) {
//         // __schema tracking
//         await db
//           .table(META)
//           .put({ id: store, version, modified: new Date() }, store);
//         // storeObject.version (if present)
//         try {
//           const meta = await db.table(store).get(store);
//           if (meta) {
//             meta.version = version;
//             meta.modified = new Date();
//             await db.table(store).put(meta);
//           }
//         } catch (_) {}
//       })(db, store, decision.chosen);

//       // Update loop's prev baseline
//       prev = decision.chosen;
//     }
//   }

//   // previous
//   //   for (const store of perStore) {
//   //     const timeline = getStoreTimeline(configs, store);
//   //     if (timeline.length === 0) continue;

//   //     const prev = await getPrevVersion(db, store);
//   //     for (const step of timeline.sort((a, b) =>
//   //       cmpTriplet(a.version, b.version)
//   //     )) {
//   //       if (cmpTriplet(step.version, prev) > 0) {
//   //         await applyConfigStep(db, store, step, {
//   //           [store]: collectModelsForStore(timeline, store),
//   //         });
//   //         await setVersion(db, store, step.version);
//   //       }
//   //     }
//   //   }

//   return db;
// }
